import {
    useEffect,
    useState
} from "react";
import {
    Animated,
    Platform
} from "react-native";
import type {
    ProgressAnnouncementConstantType,
    ProgressValueTextConstantType,
    ProgressValueConstantType,
    ProgressCustomThemeType,
    ProgressMaxConstantType,
    ProgressValueText
} from "./type";
import {
    useAnimatedState,
    MOTION_EASINGS
} from "../../helpers/motion";
import {
    formatPercent,
    formatNumber
} from "../../helpers/locale";

export const PROGRESS_STEPS_LIMITS = {
    max: 10,
    min: 2
};

export const PROGRESS_DEFAULT_MAX = 100;

export const PROGRESS_ANNOUNCE_BUCKETS = 4;

export const PROGRESS_INDETERMINATE_RATIO = 0.4;

export const PROGRESS_INDETERMINATE_ARC = 0.25;

const PROGRESS_EASING_SAMPLES = 16;

export const getProgressMax = ({
    variant,
    steps,
    max
}: ProgressMaxConstantType): number => {
    if(variant === "segmented") {
        return Math.min(Math.max(Math.round(steps ?? PROGRESS_STEPS_LIMITS.min), PROGRESS_STEPS_LIMITS.min), PROGRESS_STEPS_LIMITS.max);
    }

    return max > 0 ? max : PROGRESS_DEFAULT_MAX;
};

export const getProgressValue = ({
    variant,
    value,
    max
}: ProgressValueConstantType): number => {
    const currentValue = Math.min(Math.max(Number.isFinite(value) ? value : 0, 0), max);

    return variant === "segmented" ? Math.round(currentValue) : currentValue;
};

export const getProgressValueText = ({
    valueFormatter,
    localize,
    variant,
    locale,
    value,
    max
}: ProgressValueTextConstantType): ProgressValueText => {
    if(valueFormatter) {
        const formattedValue = valueFormatter(value, max);

        return {
            accessibleText: formattedValue,
            valueText: formattedValue
        };
    }

    if(variant === "segmented") {
        const formattedStep = formatNumber({
            locale,
            value
        });

        const formattedMax = formatNumber({
            value: max,
            locale
        });

        return {
            valueText: `${formattedStep} / ${formattedMax}`,
            accessibleText: localize("progress-step-of", [
                formattedStep,
                formattedMax
            ])
        };
    }

    const percentText = formatPercent({
        value: value / max,
        locale
    });

    return {
        accessibleText: percentText,
        valueText: percentText
    };
};

export const getProgressAnnouncement = ({
    accessibleText,
    title
}: ProgressAnnouncementConstantType): string => {
    return [
        title,
        accessibleText
    ].filter((part) => part).join(": ");
};

export const getEasedRange = ({
    from,
    to
}: {
    from: number;
    to: number;
}) => {
    const inputRange: Array<number> = [];

    const outputRange: Array<number> = [];

    for(let index = 0; index <= PROGRESS_EASING_SAMPLES; index += 1) {
        const progress = index / PROGRESS_EASING_SAMPLES;

        inputRange.push(progress);

        outputRange.push(from + (to - from) * MOTION_EASINGS.inOut(progress));
    }

    return {
        outputRange,
        inputRange
    };
};

export const useAnimatedNumber = ({
    customTheme,
    value
}: {
    customTheme?: ProgressCustomThemeType;
    value: number;
}): number => {
    const [
        currentValue,
        setCurrentValue
    ] = useState<number>(value);

    const animatedValue = useAnimatedState({
        isNativeDriverEnabled: false,
        intent: "valueChange",
        customTheme,
        value
    });

    useEffect(() => {
        const listenerID = animatedValue.addListener(({
            value: nextValue
        }) => {
            setCurrentValue(nextValue);
        });

        return () => {
            animatedValue.removeListener(listenerID);
        };
    }, [
        animatedValue
    ]);

    return currentValue;
};

export const useProgressLoop = ({
    duration,
    isActive,
    shape
}: {
    shape: "breathe" | "linear";
    isActive: boolean;
    duration: number;
}): Animated.Value => {
    const [
        value
    ] = useState(() => new Animated.Value(0));

    useEffect(() => {
        value.setValue(0);

        if(!isActive || !duration || Platform.OS === "web") {
            return;
        }

        const useNativeDriver = true;

        const animation = shape === "breathe" ? Animated.loop(Animated.sequence([
            Animated.timing(value, {
                easing: MOTION_EASINGS.inOut,
                duration: duration / 2,
                isInteraction: false,
                useNativeDriver,
                toValue: 1
            }),
            Animated.timing(value, {
                easing: MOTION_EASINGS.inOut,
                duration: duration / 2,
                isInteraction: false,
                useNativeDriver,
                toValue: 0
            })
        ])) : Animated.loop(Animated.timing(value, {
            easing: MOTION_EASINGS.linear,
            isInteraction: false,
            useNativeDriver,
            toValue: 1,
            duration
        }));

        animation.start();

        return () => {
            animation.stop();
        };
    }, [
        isActive,
        duration,
        shape,
        value
    ]);

    return value;
};
